import { useState } from 'react';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, getDoc, collection, query, where, getDocs, serverTimestamp, getFirestore } from 'firebase/firestore';
import { auth, linkSubmissionToUser } from '../services/firebase';
import firebaseConfig from '../services/firebase/firebase-config';
import { useAuthContext } from './useAuthContext';

export const useGoogleSignIn = () => {
  const [error, setError] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const { dispatch } = useAuthContext();

  const createUserProfile = async (user, submissionId = null) => {
    try {
      console.log(`Creating user profile for Google user: ${user.uid}, email: ${user.email}`);
      
      const db = getFirestore();
      
      // Check if there's existing waitlist data for this user's email to preserve the link
      let waitlistSubmissionId = submissionId;
      
      // Try to find waitlist entry by email if not already provided (simplified lookup)
      if (user.email && !submissionId) {
        const waitlistRef = collection(db, "waitlist");
        const emailQuery = query(waitlistRef, where("userInfo.email", "==", user.email));
        const emailQuerySnapshot = await getDocs(emailQuery);
        
        if (!emailQuerySnapshot.empty) {
          const docSnap = emailQuerySnapshot.docs[0];
          waitlistSubmissionId = docSnap.id;
          console.log(`Found waitlist data for email ${user.email}, ID: ${waitlistSubmissionId}`);
          
          // Update the submission with userId if not already set
          const waitlistData = docSnap.data();
          if (!waitlistData.userId) {
            await setDoc(doc(db, "waitlist", waitlistSubmissionId), {
              userId: user.uid,
              linkedAt: serverTimestamp()
            }, { merge: true });
            console.log(`Updated waitlist entry with user ID: ${user.uid}`);
            
            // Create mapping for future lookups
            await setDoc(doc(db, "user_submissions_map", user.uid), {
              submissionId: waitlistSubmissionId,
              linkedAt: serverTimestamp()
            });
            console.log("Created mapping document");
          }
        } else {
          console.log(`No waitlist entry found for email ${user.email}`);
        }
      }
      
      // Extract first and last name from displayName for Airtable sync consistency
      const nameParts = user.displayName ? user.displayName.trim().split(' ') : ['', ''];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      // Create minimal user profile data for new onboarding flow
      const userData = {
        uid: user.uid,
        userId: user.uid,
        email: user.email || null,
        displayName: user.displayName || null,
        photoURL: user.photoURL || null,
        phoneNumber: user.phoneNumber || null,
        // Direct signups through /auth are active users, not waitlist
        status: 'active',
        // Add basicInfo structure for Airtable sync consistency
        basicInfo: {
          firstName: firstName,
          lastName: lastName,
          email: user.email || null,
          name: user.displayName || ''
        },
        // Reference to waitlist submission if found
        waitlistSubmissionId,
        // NEW FLAG - indicates user needs to complete onboarding
        onboardingComplete: false,
        // Timestamps and metadata
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        emailVerified: user.emailVerified,
        isAdmin: user.email === 'grey@justburnt.com' || 
                user.email === 'arjan@justburnt.com' || 
                user.email === 'louie@justburnt.com'
      };
      
      // Check if profile already exists
      const profileRef = doc(db, "user_profiles", user.uid);
      const profileSnap = await getDoc(profileRef);
      
      if (!profileSnap.exists()) {
        // Create a document in user_profiles collection
        await setDoc(profileRef, userData);
        console.log(`Created user profile document for ${user.uid}`);
      } else {
        // Profile exists - for Google sign-in, check if user is returning or new
        const existingData = profileSnap.data();
        if (existingData.onboardingComplete !== true) {
          // If onboarding isn't complete, preserve waitlist status
          // Only set to active if user is not on waitlist
          const updateData = {
            ...existingData,
            onboardingComplete: false,
            lastLogin: serverTimestamp()
          };
          
          // Only update status to active if user is not currently on waitlist
          if (existingData.status !== 'waitlist') {
            updateData.status = 'active';
          }
          
          await setDoc(profileRef, updateData, { merge: true });
          console.log(`Updated existing profile for ${user.uid} - status: ${updateData.status || existingData.status}`);
        } else {
          // Just update last login for returning users
          await setDoc(profileRef, {
            lastLogin: serverTimestamp()
          }, { merge: true });
          console.log(`Updated last login for returning user ${user.uid}`);
        }
      }
      
      // Create an empty profile summary document if it doesn't exist
      const profileSummaryRef = doc(db, "user_profile_summaries", user.uid);
      const profileSummarySnap = await getDoc(profileSummaryRef);
      
      if (!profileSummarySnap.exists()) {
        const defaultProfileSummary = {
          userID: user.uid,  // Add explicit userID reference
          profileSummary: "",
          keyHighlights: {
            role: "",
            industries: [],
            strengths: []
          },
          idealRoleCharacteristics: {
            roleScope: "",
            keyMotivators: "",
            industryFocus: []
          },
          keySkills: [],
          painPoints: [],
          potentialFit: "",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        await setDoc(profileSummaryRef, defaultProfileSummary);
        console.log(`Created profile summary document for ${user.uid}`);
      }
      
      return { success: true };
    } catch (error) {
      console.error(`Error creating user profile for ${user.uid}:`, error);
      return { success: false, error: error.message };
    }
  };

  const signInWithGoogle = async (submissionId = null, allowAccountCreation = false) => {
    setError(null);
    setIsPending(true);
    
    try {
      const provider = new GoogleAuthProvider();
      
      // Add client ID explicitly if available
      if (firebaseConfig.clientId) {
        provider.setCustomParameters({
          'client_id': firebaseConfig.clientId
        });
      }
      
      // Add scopes if needed
      // provider.addScope('https://www.googleapis.com/auth/contacts.readonly');
      
      const result = await signInWithPopup(auth, provider);
      
      // This gives you a Google Access Token
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const token = credential.accessToken;
      
      // The signed-in user info
      const user = result.user;
      
      // Check if user profile exists in Firestore
      const db = getFirestore();
      const profileRef = doc(db, "user_profiles", user.uid);
      const profileSnap = await getDoc(profileRef);
      
      if (!profileSnap.exists()) {
        // User profile doesn't exist
        if (!allowAccountCreation) {
          // Block new account creation on login screen
          // Sign out the user immediately
          await auth.signOut();
          
          const errorMsg = 'No account found. Please sign up with a valid referral code first.';
          setError(errorMsg);
          setIsPending(false);
          return { success: false, error: errorMsg };
        }
        
        // Account creation is allowed (signup flow with valid referral code)
        // Create user profile
        await createUserProfile(user, submissionId);
      } else {
        // User profile exists - this is an existing user logging in
        // Just update their last login timestamp
        await setDoc(profileRef, {
          lastLogin: serverTimestamp()
        }, { merge: true });
        console.log(`Updated last login for existing user ${user.uid}`);
      }
      
      // If a submissionId is provided, link it to the user
      if (submissionId) {
        await linkSubmissionToUser(user.uid, submissionId);
      }
      
      // Dispatch login action
      dispatch({ type: 'LOGIN', payload: user });
      
      setIsPending(false);
      setError(null);
      
      return { success: true, user };
    } catch (error) {
      console.error('Google sign-in error:', error);
      
      // Handle Errors
      const errorMessage = error.message;
      const email = error.customData?.email;
      const credential = GoogleAuthProvider.credentialFromError(error);
      
      let userMessage = 'Failed to sign in with Google';
      if (error.code === 'auth/popup-closed-by-user') {
        userMessage = 'Sign-in cancelled. Please try again.';
      } else if (error.code === 'auth/popup-blocked') {
        userMessage = 'Sign-in popup was blocked. Please allow popups for this site.';
      } else if (error.code === 'auth/operation-not-allowed') {
        userMessage = 'Google sign-in is not enabled or properly configured.';
      }
      
      setError(userMessage);
      setIsPending(false);
      return { success: false, error: userMessage };
    }
  };

  return { signInWithGoogle, error, isPending };
};